import { useCallback, useEffect, useReducer } from 'react';
import { NEW_FEATURES } from '../new-features';

const STORAGE_KEY = 'bw_seen_features';

// ── Module-level singleton so ALL hook instances share the same seen set ──────
// Any call to markSeen / markSeenForTab updates this and notifies every
// subscribed component to re-render — no need for Context or Zustand.

function readStorage(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set<string>(JSON.parse(raw) as string[]) : new Set<string>();
  } catch {
    return new Set<string>();
  }
}

let _seenIds: Set<string> = readStorage();
const _listeners = new Set<() => void>();

function broadcast(next: Set<string>) {
  _seenIds = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  } catch { /* storage unavailable — degrade gracefully */ }
  _listeners.forEach(fn => fn());
}

// ── Standalone utilities (safe to call outside React) ────────────────────────

/** Mark a feature as seen from outside a React component (e.g. NewFeaturesObserver). */
export function markSeenById(id: string): void {
  if (_seenIds.has(id)) return;
  const next = new Set(_seenIds);
  next.add(id);
  broadcast(next);
}

/** Check whether a feature has been seen (outside React). */
export function isSeenById(id: string): boolean {
  return _seenIds.has(id);
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Manages "New Feature" badge visibility.
 *
 * All hook instances share the same module-level seen set, so marking a feature
 * as seen in one component (e.g. Settings tab bar) immediately updates badges
 * in sibling components (e.g. WidgetSection field labels) without needing Context.
 *
 * - `isNew(id)`           → true if the feature is in the registry and unseen
 * - `markSeen(id)`        → marks a single feature as seen
 * - `hasNewForNav(href)`  → true if any unseen feature targets this sidebar href
 * - `hasNewForTab(tab)`   → true if any unseen feature targets this settings tab
 * - `markSeenForTab(tab)` → marks all features under a settings tab as seen
 */
export function useNewFeatures() {
  // forceUpdate lets us re-render when the shared _seenIds changes
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    _listeners.add(forceUpdate);
    return () => { _listeners.delete(forceUpdate); };
  }, []);

  const markSeen = useCallback((id: string) => {
    if (_seenIds.has(id)) return;
    const next = new Set(_seenIds);
    next.add(id);
    broadcast(next);
  }, []);

  const markSeenForTab = useCallback((settingsTab: string) => {
    const ids = NEW_FEATURES.filter(f => f.settingsTab === settingsTab).map(f => f.id);
    if (ids.length === 0) return;
    const next = new Set(_seenIds);
    ids.forEach(id => next.add(id));
    broadcast(next);
  }, []);

  const isNew = useCallback(
    (id: string): boolean => NEW_FEATURES.some(f => f.id === id) && !_seenIds.has(id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // _seenIds is module-level; forceUpdate ensures re-render on change
  );

  const hasNewForNav = useCallback(
    (navHref: string): boolean =>
      NEW_FEATURES.some(f => f.navHref === navHref && !_seenIds.has(f.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const hasNewForTab = useCallback(
    (settingsTab: string): boolean =>
      NEW_FEATURES.some(f => f.settingsTab === settingsTab && !_seenIds.has(f.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const hasNewForWidgetSection = useCallback(
    (sectionId: string): boolean =>
      NEW_FEATURES.some(f => f.widgetSection === sectionId && !_seenIds.has(f.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return { isNew, markSeen, hasNewForNav, hasNewForTab, markSeenForTab, hasNewForWidgetSection };
}
